/**
 * Settings > Geocoding (tracker item 41, ADMIN): how many service addresses
 * have been placed on the map, the company location, which geocoder the
 * server uses, and "Retry failed" (look up failed, not found and skipped
 * addresses again). The geocoder runs in the background on the server.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IGeocodingApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { secondaryButtonClass } from '../../components/formStyles';
import { StatCard } from '../../components/DetailWidgets';
import { GeocodeStatusChip, geocodeStatusLabel } from '../../components/GeocodeStatusChip';
import { Toast } from '../../components/Toast';

const PROVIDER_LABELS: Record<string, string> = {
  nominatim: 'OpenStreetMap Nominatim',
  fake: 'Test geocoder (no addresses leave the server)',
  none: 'Off (no addresses leave the server)',
};

const STATUS_ORDER: types.GeocodeStatus[] = ['OK', 'MANUAL', 'PENDING', 'NOT_FOUND', 'FAILED', 'SKIPPED'];

export function GeocodingSettingsPage() {
  const api = useMemo<IGeocodingApi>(() => createAPIClient(), []);
  const [status, setStatus] = useState<types.GeocodingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setStatus(await api.getGeocodingStatus());
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load the geocoding status'));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async () => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const result = await api.retryFailedGeocoding();
      const parts = [`${result.requeued} address${result.requeued === 1 ? '' : 'es'} queued again`];
      if (result.companyRequeued) parts.push('and the company address');
      setNotice(`${parts.join(' ')}.`);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to retry'));
    } finally {
      setBusy(false);
    }
  };

  const retryable = status ? status.properties.FAILED + status.properties.NOT_FOUND + status.properties.SKIPPED : 0;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-6">
        <Link to="/settings" className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">
          ← Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Geocoding</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Service addresses are placed on the map in the background so travel between jobs can be estimated. Saving a property never
          waits for it. Fix a wrong location on the property's page.
        </p>
      </div>

      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}

      {!status && !error && (
        <p role="status" className="text-sm text-gray-500">Loading...</p>
      )}

      {status && (
        <div className="space-y-6">
          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="geocoding-addresses-heading">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <h2 id="geocoding-addresses-heading" className="text-base font-semibold text-gray-900">
                Service addresses ({status.properties.total})
              </h2>
              <button type="button" onClick={retry} disabled={busy || (retryable === 0 && !['FAILED', 'NOT_FOUND', 'SKIPPED'].includes(status.company.geocodeStatus))}
                className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`}>
                {busy ? 'Retrying...' : 'Retry failed'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {STATUS_ORDER.map((s) => (
                <StatCard key={s} label={geocodeStatusLabel(s)} value={status.properties[s]} />
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              "Retry failed" looks up the failed, not found and skipped addresses again. Found and hand-set locations are kept.
            </p>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="geocoding-company-heading">
            <h2 id="geocoding-company-heading" className="text-base font-semibold text-gray-900 mb-2">Company location</h2>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <GeocodeStatusChip status={status.company.geocodeStatus} precision={status.company.geocodePrecision} />
              <span className="text-gray-600">
                {status.company.hasAddress
                  ? 'Every technician\'s day starts here.'
                  : 'No company address yet: add one under Settings > Company.'}
              </span>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="geocoding-provider-heading">
            <h2 id="geocoding-provider-heading" className="text-base font-semibold text-gray-900 mb-2">Geocoder</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Provider</dt>
                <dd className="text-gray-900">{PROVIDER_LABELS[status.provider] ?? status.provider}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Background lookups</dt>
                <dd className="text-gray-900">{status.workerEnabled ? 'Running' : 'Not running on this server'}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-gray-500">
              {status.provider === 'nominatim'
                ? 'Addresses are sent to OpenStreetMap Nominatim, one per second at most. Map data © OpenStreetMap contributors.'
                : 'The server administrator chooses the geocoder (GEOCODING_PROVIDER).'}
            </p>
          </section>
        </div>
      )}
    </main>
  );
}
